import { z } from "zod";
import { PrescribedConditionTypes } from "./conditions";

const generateRandomNumber = () : number => {
    const array = new Uint32Array(1);
    self.crypto.getRandomValues(array);
    return array[0];
};

const Medication = z.object({
    syntheticId: z.number().default(generateRandomNumber),
    id: z.union([z.number(), z.string()]).nullish(),
    name: z.string(),
    type: z.string().default(""),
    medicationStartDate: z.string().nullish(),
    dosage: z.number().min(0.001).max(9999.9999).nullable(),
    dosageUnits: z.string().nullable(),
    frequencyUnits: z.string().nullable(),
    frequency: z.number().nullish(),
}).passthrough();

const Condition = z.object({
    id: z.number().default(0),
    name: z.string(),
}).passthrough();

const HeartSubCondition = Condition.extend({
    diagnosisdate: z.string(),
    occurrences: z.number().min(1).max(999).nullish(),
    ongoingInd: z.string().nullish(),
    medication: z.array(Medication),
    syntheticId: z.number().default(generateRandomNumber),
}).passthrough();

const HeartCondition = Condition.extend({
    subConditions: z.array(HeartSubCondition),
    type: z.literal(PrescribedConditionTypes.Heart),
}).passthrough();

const RespiratorySubCondition = Condition.extend({
    diagnosisdate: z.string(),
    syntheticId: z.number().default(generateRandomNumber),
}).passthrough();

const RespiratoryCondition = Condition.extend({
    subConditions: z.array(RespiratorySubCondition),
    medications: z.array(Medication),
    type: z.literal(PrescribedConditionTypes.Respiratory),
}).passthrough();

const CancerCondition = Condition.extend({
    diagnosis_date: z.union([z.string(), z.null()]),
    medications: z.array(Medication),
    type: z.literal(PrescribedConditionTypes.Cancer),
}).passthrough();

const StrokeCondition = Condition.extend({
    medications: z.array(Medication),
    stroke_type_CVA: z.union([z.string(), z.null()]),
    stroke_type_SAH: z.union([z.string(), z.null()]),
    stroke_type_CHB: z.union([z.string(), z.null()]),
    stroke_type_TIA: z.union([z.string(), z.null()]),
    type: z.literal(PrescribedConditionTypes.Stroke),
}).passthrough();

const DiabetesCondition = Condition.extend({
    diagnosis_date: z.union([z.string(), z.null()]),
    medications: z.array(Medication),
    type: z.literal(PrescribedConditionTypes.Diabetes),
}).passthrough();

const HypertensionReading = z.object({
    syntheticId: z.number().default(generateRandomNumber),
    systolic: z.union([z.number(), z.null()]),
    diastolic: z.union([z.number(), z.null()]),
    readingdate: z.union([z.string(), z.null(), z.undefined()]).optional(),
}).passthrough();

const HypertensionCondition = Condition.extend({
    hypertensionDiagnosisDate: z.union([z.string(), z.null()]),
    medications: z.array(Medication),
    pressureReadings: z.array(HypertensionReading).nullish(),
    type: z.literal(PrescribedConditionTypes.Hypertension),
}).passthrough();

const CholesterolReading = z.object({
    syntheticId: z.number().default(generateRandomNumber),
    cholesterolReadingsType: z.string().nullish(),
    LDL: z.number().nullish(),
    HDL: z.number().nullish(),
    TOTAL: z.number().nullish(),
    readingdate: z.string().nullish(),
}).passthrough();

const CholesterolCondition = Condition.extend({
    diagnosis_date: z.union([z.string(), z.null()]),
    medications: z.array(Medication),
    cholesterolReadings: z.array(CholesterolReading).nullish(),
    type: z.literal(PrescribedConditionTypes.Cholesterol),
}).passthrough();

const MultiplesclerosisCondition = Condition.extend({
    multiplesclerosis_date: z.string(),
    medications: z.array(Medication),
    type: z.literal(PrescribedConditionTypes.Multiplesclerosis),
}).passthrough();

const OtherCondition = Condition.extend({
    diagnosis_date: z.union([z.string(), z.null()]),
    medications: z.array(Medication),
    type: z.literal(PrescribedConditionTypes.Other),
}).passthrough();

const NeurologicalSubCondition = Condition.extend({
    diagnosisdate: z.string(),
    syntheticId: z.number().default(generateRandomNumber),
}).passthrough();

const NeurologicalCondition = Condition.extend({
    subConditions: z.array(NeurologicalSubCondition),
    medications: z.array(Medication),
    type: z.literal(PrescribedConditionTypes.Neurological),
}).passthrough();

export const MedicalCondition = z.discriminatedUnion("type", [
    HeartCondition,
    RespiratoryCondition,
    CancerCondition,
    StrokeCondition,
    DiabetesCondition,
    HypertensionCondition,
    CholesterolCondition,
    MultiplesclerosisCondition,
    OtherCondition,
    NeurologicalCondition,
]);
